import { API_BASE, AUTH_TOKEN } from '../config';

// Thin fetch wrapper against the shared HypeTunes API.
async function get(path) {
  const headers = {};
  if (AUTH_TOKEN) headers.Authorization = `Bearer ${AUTH_TOKEN}`;
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`API ${path} -> ${res.status}`);
  return res.json();
}

// POST that returns { res, data } so callers can branch on status (the Anam
// cameo path treats 429/503 as "back off", not an error) — mirrors useVideoHost.
async function post(path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (AUTH_TOKEN) headers.Authorization = `Bearer ${AUTH_TOKEN}`;
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body || {}) });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

// Generic verb (PATCH/DELETE) for the admin placement tool — same auth + shape
// as post(), so callers branch on { res, data } identically.
async function req(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (AUTH_TOKEN) headers.Authorization = `Bearer ${AUTH_TOKEN}`;
  const res = await fetch(`${API_BASE}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

export const api = {
  // District station now-playing [reuse existing radio endpoint]
  stationNow: (slug) => get(`/api/radio/stations/${encodeURIComponent(slug)}/now`),
  // An artist's own 24/7 station (`station: null` when they have none).
  artistRadio: (avatarId) => get(`/api/artist-radio/${encodeURIComponent(avatarId)}`),
  stations: () => get('/api/radio/stations'),
  // A room's own PLAYLIST soundtrack, now (API RoomHosts.nowOf): a station /now's shape.
  roomNow: (slug) => get(`/api/world/rooms/${encodeURIComponent(slug)}/now`),
  // The Jahva House tables' headphones: every spoken-word piece performed in the city.
  spokenWord: () => get('/api/world/spoken-word'),

  // Liking what is on the radio. THE EXISTING ENDPOINT — track likes live under
  // /api/radio, not /api/tracks, which is the only reason they take finding.
  // Same rows the website and the mobile app write, so a heart pressed in the
  // world shows up in Liked everywhere else, and the owner gets the same
  // `track_like` notification. POST is INSERT IGNORE, so a double-tap is free.
  likeTrack: (trackId) => post('/api/radio/likes', { trackId }),
  unlikeTrack: (trackId) => req('DELETE', `/api/radio/likes/${trackId}`),

  // A booked campaign's artwork — what a placement with a `<source>:<ref>`
  // content policy rotates through. Public, like the walls themselves; URLs are
  // signed per request by the API and never stored signed.
  adCreatives: (advertiserRef, source = 'quickshout') => get(
    `/api/ads/creatives/${encodeURIComponent(advertiserRef)}?source=${encodeURIComponent(source)}`,
  ),
  // The newest published posts — what a "Latest posts" board shows (houseFill).
  // The website's public feed: newest first, URLs signed, archived media and
  // (signed in) blocked artists already left out.
  latestPosts: (limit = 24) => get(`/api/posts/feed?limit=${Number(limit) || 24}`),
  // HYPE-U (city/hypeu): the learn page's courses — hosts, lessons in their own
  // words — and a page of a lesson read in the host's voice ({ url, seconds }).
  // Both public, as on the website's /learn.
  learn: () => get('/api/learn'),
  learnListen: (course, lesson, page) => post('/api/learn/listen', { course, lesson, page }),

  // World modulation track (compact per-track analysis) — added to the API
  // as a NEW read-only endpoint (does not change any existing behavior).
  worldModulation: (trackId) => get(`/api/world/modulation/${trackId}`),

  // World bootstrap: districts, residents, venues (NEW additive endpoint).
  worldState: () => get('/api/world/state'),

  // Player-BUILT bodies (the editor's 3D-model flow → avatar_world_models).
  // Merged into npcList beside the git-tracked roster so Peers / embodiment /
  // the character select resolve a user-built body exactly like a hand-built
  // one. Public, and the URLs are the cache-stable assets proxy — never
  // signed, so a returning player's browser cache actually works. Absolutised
  // here so callers treat them like any other model URL.
  // The player mannequins (API migration 464; game/mannequins). `ready: false` =
  // the table is not there yet and the World keeps its bundled set.
  worldMannequins: async () => {
    const data = await get('/api/world/mannequins');
    const abs = (u) => (u && u.startsWith('/') ? `${API_BASE}${u}` : u);
    return { ready: !!data?.ready, list: (data?.mannequins || []).map((m) => ({ ...m, url: abs(m.url), thumb: abs(m.thumb) })) };
  },
  // Who flies each heli-tour pad: { 'pad-1': { avatarId, name } } (API GET
  // /api/world/heli/pilots; cast members whose home is heliport:pad-N).
  heliPilots: async () => (await get('/api/world/heli/pilots'))?.pilots || {},
  avatarModels: async () => {
    const data = await get('/api/world/avatar-models');
    return (data?.models || []).map((m) => ({
      ...m,
      /* THE KTX2 TWIN WHEN THERE IS ONE (API `riggedKtx2`, migration 445): the
         same body, textures ~8× smaller on the GPU (the ICT bodies crashed iPhone
         Safari as JPEG, 2026-10-29). The World transcodes KTX2 (ktx2Extend), so it
         takes it; `rigged` is the plain file for clients that cannot. */
      rigged: (m.riggedKtx2 || m.rigged) ? `${API_BASE}${m.riggedKtx2 || m.rigged}` : null,
      walk: m.walk ? `${API_BASE}${m.walk}` : null,
      idle: m.idle ? `${API_BASE}${m.idle}` : null,
      /* ⚠ `pose` IS NOT ALL NUMBERS ANY MORE. It carries the body's rig
         corrections (shoulderDrop, elbowIn, …) — which are — and, since the
         speaking mouth landed, ONE FILE: `pose.face.video`, handed over as a
         path on the API's asset proxy exactly like the three above. Spreading
         `...m` carried it through untouched, so a World running against a
         remote API asked its OWN origin for the clip and got the app shell back
         — HTTP 200, text/html, no error, no face. Absolutised here with
         everything else it travels with. */
      pose: m.pose?.face?.video?.startsWith('/')
        ? { ...m.pose, face: { ...m.pose.face, video: `${API_BASE}${m.pose.face.video}` } }
        : (m.pose || null),
      /* ⚠ AND THE GESTURE MAP, for the same reason. `clips` is `{listening_gesture:
         '/api/assets/file?…', big_wave_hello: …}` — the body's extra slate beyond
         walk and idle, and every value is a path on the API, not on this origin.
         It sat unabsolutised because until the resting pose landed nothing in the
         World ever loaded one. */
      clips: m.clips && typeof m.clips === 'object'
        ? Object.fromEntries(Object.entries(m.clips)
          .map(([k, u]) => [k, typeof u === 'string' && u.startsWith('/') ? `${API_BASE}${u}` : u]))
        : null,
    }));
  },

  // Featured tracks — the same list /listen + the landing page use. Public,
  // returns signed artwork_url + audio_url + title + avatar_name per track.
  featuredTracks: () => get('/api/tracks/featured'),

  // Music videos (the /listen discover reel). gcs_url + aspect_ratio + title +
  // avatar_name; mostly 16:9. Used for moving-clip billboards.
  musicVideos: (limit = 24) => get(`/api/music-videos/discover?limit=${limit}`),

  // Podcast episodes — the same published list /listen reads. Signed
  // cover_image_url + host portraits, plus `guest_avatar_id`, which is the only
  // PER-EPISODE way to tell an interview from a news/banter show (show_format
  // lives on the host SET, so one set producing both reports the same value for
  // either). Feeds the interview-podcast and news-broadcast placements.
  podcastEpisodes: (limit = 24) => get(`/api/podcast/episodes?limit=${limit}`),

  // HypeClips — the /listen "HypeClips" row. Vertical 9:16 narrative clips
  // (avatar_videos, video_type='hypeclip', featured). Signed gcs_url + title +
  // avatar_name + track_id. Played on the tall city signs.
  hypeClips: (limit = 24) => get(`/api/videos/hypeclips/featured?limit=${limit}`),

  // The venues the town voted for (API city/VenueRegistry): each one's site,
  // name, look, hours and host, with the host's pictures for its wall screens.
  // Public. Read by components/VenueRegistrySync; a 404 (an API that predates
  // the registry) throws, which that component reads as "no venues".
  worldVenues: () => get('/api/world/venues'),

  // Featured artists (is_featured_artist = 1, active) — the NPC roster source.
  // avatar_id + name + handle + main_genre + profile_image_url. The World joins
  // this against models/avatars/roster.json to place engageable resident NPCs.
  featuredArtists: () => get('/api/featured-artists/showcase'),

  // Who this viewer follows → { following: [{ avatar_id, … }] }. The map marks
  // the ones present as residents (followed ∩ roster) with a headshot pin.
  following: () => get('/api/feed/following/list'),

  // This viewer's OWN avatars → { avatars: [{ avatar_id, … }] } (findByUserId).
  // The map marks these too — "mine," distinct from "following."
  myAvatars: () => get('/api/avatars'),
  // A NEW LIFE. Created empty on purpose — the come-up is what gives an artist
  // a name, a look and a first record, so pre-filling any of it skips the game.
  createAvatar: (body) => post('/api/avatars', body || {}),
  // Which intro ride runs — Spor1 always drives; the server decides whether he
  // recognises you (canon, not a client preference). Build_Game_Intro §4.2/§5.1.
  dialogueRide: (avatarId) => get(`/api/dialogue/ride?avatarId=${avatarId}`),
  // The ride's authored landmark lore — Spor1's line + cached clip per venue.
  // Guest-safe. audioUrl is null until his voice_id lands; line always comes.
  rideReveals: () => get('/api/dialogue/ride/reveals'),
  // Lyra's walk nudges with cached clips in her call voice. Guest-safe.
  walkLines: () => get('/api/dialogue/walk/lines'),

  // ── The salon's top-up surface (money = conventional UI, design law 1) ──
  // Paid plans carry monthly_credits; the hosted checkout is where the card
  // is entered. The world never touches payment details itself.
  // One avatar, full row (appearance + signed profile_image_url) — the salon
  // uses it to know whether a look was authored but never pictured.
  avatarDetail: (id) => get(`/api/avatars/${id}`),
  // THE LOOK = THE FUNNEL CREATOR (Khari, 2026-08-03: "use the existing
  // funnel avatar creator we have already written" — pose-mode direct gen
  // made default-faced strangers). Character imagination → 5 distinct
  // variants sharing the STATED identity → picking one promotes its bible
  // to drive every future image. Cached sets return instantly; regenerate
  // replaces.
  //
  // ALWAYS NAME THE AVATAR. Left unsaid the server falls back to the user's
  // NEWEST avatar — correct for the web funnel, which has exactly one, and
  // wrong every time here: you walk into that salon as whoever you entered the
  // world as, and a roster owner is rarely wearing their newest face. Unnamed,
  // the salon styles the artist in the chair and hangs the picture on someone
  // else (Khari, 2026-08-06 — found while the chair itself was being fixed).
  funnelHeadshots: (regenerate, avatarId) => post('/api/funnel/generate-headshots', { regenerate: !!regenerate, avatarId }),
  funnelSelectHeadshot: (imageId, avatarId) => post('/api/funnel/select-headshot', { imageId, avatarId }),
  // The 3D body queues from the chosen face (idempotent; comp/charge rules
  // live server-side — refusal codes come back, never thrown).
  requestWorldEntry: (id) => post(`/api/world/avatar-entry/${id}`),
  plans: () => get('/api/subscriptions/plans'),
  planCheckout: (body) => post('/api/subscriptions/create-checkout-session', body),
  creditBalance: () => get('/api/subscriptions/credits/balance'),
  // The walk's producer lineup (§4.4) — Lyra's text renders these as cards.
  dialogueProducers: () => get('/api/dialogue/producers'),
  // Who is on the line — name + SIGNED face for the phone's call screens.
  // Guest-safe (above the story gate). npcFromAvatar scenes (the handoff)
  // need avatarId so "your artist calling" wears the right face.
  dialogueCaller: (sceneKey, avatarId) =>
    get(`/api/dialogue/caller/${encodeURIComponent(sceneKey)}${avatarId ? `?avatarId=${avatarId}` : ''}`),
  // Who the viewer is, to the API rather than to the token. The JWT says userId
  // and not much else; `hasPlan` (an active subscription, not merely a plan row)
  // only exists here, and it is what the access ladder turns into a rung.
  me: () => get('/api/auth/me'),
  // Whether an avatar has a built world model — the top rung. Throws 404 when no
  // entry row exists, which is the ordinary "no body" case, not an error.
  avatarEntry: (avatarId) => get(`/api/world/avatar-entry/${avatarId}`),
  // Has this artist earned the studio — the ride, then the salon.
  storyGate: (avatarId) => get(`/api/world/story-gate/${avatarId}`),
  // An artist's tracks in PORTFOLIO order (pinned first, then newest) — the same
  // ordering the web portfolio uses. Released-only for visitors; URLs signed.
  avatarTracks: (avatarId) => get(`/api/tracks/avatar/${avatarId}`),
  // Ad/promo display-surface inventory (public read; admin writes).
  // `all` includes surfaces that have been HIDDEN (active = 0). The world never
  // asks for those; the admin panel does, because a hidden placement you cannot
  // list is a deleted one with extra steps.
  placements: (all) => get(`/api/world/placements${all ? '?all=1' : ''}`),
  createPlacement: (body) => post('/api/world/placements', body),
  // Portal placement (POC): trade a captured wall for a live Happy Oyster
  // session seeded with it. Returns { res, data } so the caller can show the
  // server's own refusal (402 out of credits, 403 admin-only) verbatim.
  openPortal: (body) => post('/api/reactor-helios/portal', body),
  updatePlacement: (id, body) => req('PATCH', `/api/world/placements/${id}`, body),
  deletePlacement: (id) => req('DELETE', `/api/world/placements/${id}`),

  // ── Radio VIDEO HOST cameo (the DJ on the big billboards) — same endpoints
  // the website's useVideoHost drives. All authed; the cameo is a paid-plan +
  // server-appearance-budget gate (no credit charge). stationId is numeric
  // (read station.station_id off the now-playing payload).
  anamRadioStatus: (stationId) => get(`/api/anam/radio/${stationId}/status`),
  anamRadioSessionToken: (stationId) => post(`/api/anam/radio/${stationId}/session-token`, {}),
  anamSessionTurn: (sessionId, body) => post(`/api/anam/session/${sessionId}/turn`, body),
  anamSessionReport: (sessionId, body) => post(`/api/anam/session/${sessionId}/report`, body)
};